import {
	type ContextualCTA,
	ExamDifficulty,
	UserAction,
	UserType,
} from "@brookbush/types";

import {
	AwardIcon,
	BookOpenIcon,
	ClapperboardIcon,
	ClockFadingIcon,
	GraduationCapIcon,
	HighlighterIcon,
	LibraryIcon,
	SlidersHorizontalIcon,
	SparklesIcon,
	VideoIcon,
	WaypointsIcon,
} from "lucide-react";
import { Inter } from "next/font/google";

import { GET_STARTED_BAR_GUEST_DESCRIPTION } from "~/lib/get-started-bar-copy";
import { PROGRAM_GUEST_SIGNUP_DESCRIPTION } from "~/lib/programs/programSignupCopy";

export const examDifficultyMap = {
	[ExamDifficulty.UNRATED]: {
		title: "Unrated",
		textColor: "text-gray-700 dark:text-gray-300",
		color: "bg-gray-500 dark:bg-gray-400",
	},
	[ExamDifficulty.VERY_EASY]: {
		title: "Very Easy",
		textColor: "text-sky-700 dark:text-sky-300",
		color: "bg-sky-500 dark:bg-sky-400",
	},

	[ExamDifficulty.EASY]: {
		title: "Easy",
		textColor: "text-emerald-700 dark:text-emerald-300",
		color: "bg-emerald-500 dark:bg-emerald-400",
	},
	[ExamDifficulty.MEDIUM]: {
		title: "Medium",
		textColor: "text-orange-700 dark:text-orange-300",
		color: "bg-orange-500 dark:bg-orange-400",
	},
	[ExamDifficulty.HARD]: {
		title: "Hard",

		textColor: "text-red-700 dark:text-red-300",
		color: "bg-red-500 dark:bg-red-400",
	},
	[ExamDifficulty.VERY_HARD]: {
		title: "Very Hard",
		textColor: "text-purple-700 dark:text-purple-300",
		color: "bg-purple-500 dark:bg-purple-400",
	},
};

export const font = Inter({
	subsets: ["latin"] as const,
	variable: "--font-inter",
	preload: true,
});

export const restrictedPaths = [
	"/about",
	"/sign-up",
	"/sign-in",
	"/become-a-member",
	"/membership",
	"/account",
	"/exam-portal",
];

export const sidebarLinks = [
	{
		label: "Learning Plan",
		shortLabel: "Home",
		href: "/learning-plan",
		icon: GraduationCapIcon,
	},

	{
		label: "Programs",
		href: "/programs",
		isBeta: true,
		icon: WaypointsIcon,
	},

	{
		label: "AI Tutor",
		href: "/ai-tutor",
		isBeta: true,
		icon: SparklesIcon,
	},

	{
		label: "Recently Visited",
		shortLabel: "Recents",
		href: "/recently-visited",
		icon: ClockFadingIcon,
	},

	{
		label: "My List",
		href: "/my-list",
		icon: LibraryIcon,
	},

	{
		label: "Certificates",
		href: "/certificates",
		icon: AwardIcon,
	},

	{
		label: "Notes",
		href: "/annotations",
		icon: HighlighterIcon,
	},
	{
		label: "Preferences",
		href: "/preferences",
		icon: SlidersHorizontalIcon,
	},
];

export const FEATURES = [
	{
		title: "4 Advanced Credentials",
		description: "BI-CPT, HMS, SPC, and IMT",
		icon: AwardIcon,
		className:
			"bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200",
	},

	{
		title: "200+ Courses",
		description: "Accredited for CEUs by ACE, ISSA, & more",
		icon: BookOpenIcon,
		className: "bg-sky-200 text-sky-800 dark:bg-sky-800 dark:text-sky-200",
	},

	{
		title: "NCCA-accredited CPT Exam Study Plan",
		description: "Lesson plan, practice exam, exam coach, and AI tutor",
		icon: GraduationCapIcon,
		className:
			"bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200",
	},

	{
		title: "500+ Videos",
		description: "High-quality, in-depth educational content",
		className: "bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200",

		icon: VideoIcon,
	},

	{
		title: "Program Generator",
		description: "Generate custom programs in seconds",
		icon: WaypointsIcon,
		className:
			"bg-violet-200 text-violet-800 dark:bg-violet-800 dark:text-violet-200",
	},

	{
		title: "AI Exam Coach and Tutor",
		description: "Personalized learning powered by AI",
		icon: SparklesIcon,
		className:
			"bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200",
	},

	{
		title: "Webinars",
		description: "Exclusive access to expert-led webinars",
		icon: ClapperboardIcon,
		className: "bg-cyan-200 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-200",
	},
];

export const CONVERSION_PAGE_TO_CONTENT_MAP: Record<
	string,
	Partial<Record<UserType, ContextualCTA>>
> = {
	"/info/programs": {
		[UserType.Guest]: {
			title: "Create Your First Program",
			description: PROGRAM_GUEST_SIGNUP_DESCRIPTION,
			buttonText: "Create an Account",
			mobileButtonText: "Sign Up",
			userAction: UserAction.WELCOME,
		},

		[UserType.User]: {
			title: "Create Unlimited Programs",
			description:
				"Become a member to create unlimited custom programs tailored to your goals.",
			buttonText: "Become a Member",
			mobileButtonText: "Join",
			userAction: UserAction.BECOME_A_MEMBER,
		},
	},
	"/programs": {
		[UserType.Guest]: {
			title: "Create Your First Program",
			description: PROGRAM_GUEST_SIGNUP_DESCRIPTION,
			buttonText: "Create an Account",
			mobileButtonText: "Sign Up",
			userAction: UserAction.WELCOME,
		},

		[UserType.User]: {
			title: "Create Unlimited Programs",
			description:
				"Become a member to create unlimited custom programs tailored to your goals.",
			buttonText: "Become a Member",
			mobileButtonText: "Join",
			userAction: UserAction.BECOME_A_MEMBER,
		},
	},

	"/programs/:id/invitations/:invitationId": {
		[UserType.Guest]: {
			title: "Accept Your Invitation",
			description:
				"Sign up now to accept your program invitation and join the community!",
			buttonText: "Create an Account",
			mobileButtonText: "Sign Up",
			userAction: UserAction.SIGN_UP,
		},

		[UserType.User]: {
			title: "Create Unlimited Programs",
			description:
				"Become a member to create unlimited custom programs tailored to your goals.",
			buttonText: "Become a Member",
			mobileButtonText: "Join",
			userAction: UserAction.BECOME_A_MEMBER,
		},
	},
	"/courses/:slug": {
		[UserType.Guest]: {
			title: "Start Here!",
			description:
				"Start with a practice exam to establish your baseline and get custom coaching recommendations!",
			buttonText: "Start Here!",
			mobileButtonText: "Start Here!",
			userAction: UserAction.START_PRACTICE_EXAM,
		},
		[UserType.User]: {
			title: "Become a Member",
			description:
				"One simple plan unlocks everything. No bundles. No add-ons.",
			buttonText: "Become a Member",
			mobileButtonText: "Join",
			userAction: UserAction.BECOME_A_MEMBER,
		},
	},

	default: {
		[UserType.Guest]: {
			title: "Get Started",
			description: GET_STARTED_BAR_GUEST_DESCRIPTION,
			buttonText: "Get Started",
			mobileButtonText: "Get Started",
			userAction: UserAction.WELCOME,
		},
		[UserType.User]: {
			title: "Become a Member",
			description:
				"One simple plan unlocks everything. No bundles. No add-ons.",
			buttonText: "Become a Member",
			mobileButtonText: "Join",
			userAction: UserAction.BECOME_A_MEMBER,
		},
	},
};
